import { useCallback, useEffect, useRef, useState } from "react";

// Display copy mirrors the fixed server prompt; only the request UUID is sent.
export const PROMOTIONS_PROMPT =
  "Help me get more from my travel promotions. Consider both my upcoming trips and new travel ideas. Use my loyalty memberships, status, credit cards, and travel preferences to identify useful offers and combinations. Prioritize value with minimal extra spending or effort. For each recommendation, explain where I could use it, what I could earn, what I need to do, and the deadlines. Check whether already-booked trips qualify, flag anything you can't confirm, and don't assume I've registered. Check whether offers can actually stack and whether the booking channel qualifies. Use current search results for prices and availability.";
export type PromotionSendOptions = { promotionRequestId?: string };

export function usePromotionStart({
  intent,
  requestId,
  existingSession,
  currentSession,
  ready,
  send,
}: {
  intent?: string | null;
  requestId?: string | null;
  existingSession?: string | null;
  currentSession?: string | null;
  ready: boolean;
  send: (text: string, options?: PromotionSendOptions) => Promise<void>;
}) {
  // The ref guards effect replay; the server owns durable request deduplication.
  const attempted = useRef<string | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(false);
  const acknowledged = useRef(Boolean(currentSession || existingSession));
  useEffect(() => {
    acknowledged.current = Boolean(currentSession || existingSession);
  }, [currentSession, existingSession]);
  const [failed, setFailed] = useState(false);
  const valid =
    intent === "promotions" &&
    typeof requestId === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      requestId
    );
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const start = useCallback(() => {
    if (
      !valid ||
      !requestId ||
      !ready ||
      acknowledged.current ||
      inFlight.current
    )
      return;
    attempted.current = requestId;
    inFlight.current = true;
    setFailed(false);
    void Promise.resolve(
      send(PROMOTIONS_PROMPT, { promotionRequestId: requestId })
    )
      .catch(() => undefined)
      .finally(() => {
        inFlight.current = false;
        if (mounted.current && !acknowledged.current) setFailed(true);
      });
  }, [valid, requestId, ready, send]);
  useEffect(() => {
    if (attempted.current !== requestId) start();
  }, [requestId, start]);
  return {
    failed: failed && valid && ready && !currentSession && !existingSession,
    retry: start,
  };
}
